﻿import { useModal } from '../../../application/hooks/useModal';
import { FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import styles from './useMonitoringExport.module.scss';
import { HookFormCheckboxGroupField } from '../../../hookFormControls/HookFormCheckboxGroupField';
import { HookFormRadioGroup } from '../../../hookFormControls/hookFormRadioGroup';
import { generateConfigurableMonitoringFormsReport, generateMonitoringFormsReport } from '../../report';
import { triggerDownloadFileDialog } from '../../report/report-helpers';
import { useContextSelector } from 'use-context-selector';
import { IssuesContext } from '../../../components/issue/provider/issues.context';
import { useMonitoringPageContext } from '../MonitoringPage';
import { IssuesStateEnum, StudyRoleClaimValue } from '../../../services/api/api-client';
import { DateFormats, localFormat } from '../../../helpers/date-helpers';
import { useStaffProfile, useStudy } from 'src/helpers/hooks/useStudy';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { usePersonNamesOptions } from '../monitoringForms/fieldOptions';
import { getMonitoringForms as getGeneralMonitoringResults } from 'src/services/api/api-client/MonitoringFormsClient';
import { normalizeFileName } from 'src/helpers/string-helper';
import {
  getMonitoringFormConfigs,
  getMonitoringFormResults,
} from 'src/services/api/api-client/ConfigurableMonitoringFormsClient';

type FormState = {
  itemsToExport: ('issues' | 'monitoringReport')[] | null;
  issuesToFilter: 'filtered' | 'all';
};

export const useMonitoringExportModal = () => {
  const modal = useModal('CLOSED');
  const personNamesOptions = usePersonNamesOptions();
  const { hasStudyClaim } = useHasStudyClaims();

  const { id: studyId, title, studyNumber, monitoringFormConfigs } = useStudy() ?? {};
  const { profile } = useStaffProfile();

  const { generateReport: generateIssuesReport } = useContextSelector(IssuesContext, (x) => x);
  const { queryParams } = useMonitoringPageContext();

  const form = useForm<FormState>({
    defaultValues: {
      issuesToFilter: 'all',
      itemsToExport: [],
    },
  });

  const getConfigurableMonitoringFormsPdf = useCallback(async () => {
    const monitoringFormResults = (
      await Promise.all(monitoringFormConfigs?.map((config) => getMonitoringFormResults(config.configId)) ?? [])
    ).flatMap((result) => result);
    const formConfigIds = [...new Set(monitoringFormResults.map((x) => x.formConfigId))];
    const formConfigs = await getMonitoringFormConfigs(studyId, formConfigIds);
    return generateConfigurableMonitoringFormsReport({
      resultsWithConfig: monitoringFormResults.map((result) => ({
        result: result,
        config: formConfigs.find((c) => c.id === result.formConfigId)!,
      })),
      studyName: title!,
      studyNumber: studyNumber!,
      authorFullName: profile.fullName,
    });
  }, [monitoringFormConfigs, profile.fullName, studyId, studyNumber, title]);

  const getHardcodedMonitoringFormsPdf = useCallback(async () => {
    const monitoringForms = await getGeneralMonitoringResults(studyId);
    return generateMonitoringFormsReport({
      forms: monitoringForms,
      studyName: title!,
      studyNumber: studyNumber!,
      authorFullName: profile.fullName,
      participants: personNamesOptions,
    });
  }, [personNamesOptions, profile.fullName, studyId, studyNumber, title]);

  const onExport = form.handleSubmit(async (values) => {
    if (!profile) return;

    modal.closeModal();

    if (values.itemsToExport?.includes('issues')) {
      if (values.issuesToFilter === 'filtered') {
        await generateIssuesReport?.({
          patientUid: queryParams.patientUniqueId ?? undefined,
          state: queryParams.issueState ? IssuesStateEnum[queryParams.issueState] : undefined,
          subjectList: queryParams.subjects ?? undefined,
          patientGroupList: queryParams.groupIds ?? undefined,
        });
      } else {
        await generateIssuesReport?.();
      }
    }

    if (
      values.itemsToExport?.includes('monitoringReport') &&
      !!studyId &&
      hasStudyClaim(StudyRoleClaimValue.MonitoringReportView)
    ) {
      const blob = monitoringFormConfigs?.length
        ? await getConfigurableMonitoringFormsPdf()
        : await getHardcodedMonitoringFormsPdf();
      const fileName = `Study-${studyNumber}-Monitoring-Report-${localFormat(new Date(), DateFormats.forFiles)}`;
      triggerDownloadFileDialog(blob, normalizeFileName(fileName));
    }
  });

  return {
    open: () => {
      form.reset({ itemsToExport: [], issuesToFilter: 'all' });
      modal.openModal();
    },
    element: (
      <FormProvider {...form}>
        <MonitoringExportModal visible={modal.visible} onHide={modal.closeModal} onExport={onExport} />
      </FormProvider>
    ),
  };
};

const MonitoringExportModal: FC<{ visible: boolean; onHide: () => void; onExport: () => void }> = (props) => {
  const { t } = useScopedTranslation('Monitoring.Export.Modal');
  const { hasStudyClaim } = useHasStudyClaims();

  const form = useFormContext<FormState>();

  const anyOptionSelected = !!form.watch('itemsToExport')?.length;

  const exportItemOptions = useMemo(
    () =>
      conditionalConcat(
        hasStudyClaim(StudyRoleClaimValue.IssueView) && [
          {
            value: 'issues',
            text: t('ExportItems.Options.Issues'),
          },
        ],
        hasStudyClaim(StudyRoleClaimValue.MonitoringReportView) && [
          {
            value: 'monitoringReport',
            text: t('ExportItems.Options.MonitoringReport'),
          },
        ],
      ),
    [hasStudyClaim, t],
  );

  const exportIssuesOptions = useMemo(
    () => [
      {
        key: 1,
        value: 'filtered',
        text: t('ExportIssues.Options.Filtered'),
      },
      {
        key: 2,
        value: 'all',
        text: t('ExportIssues.Options.All'),
      },
    ],
    [t],
  );

  return (
    <DialogModal
      title={t('Title')}
      visible={props.visible}
      onHide={props.onHide}
      bodyClassName={styles.modalBody}
      footer={{
        leftButton: {
          text: t('Cancel'),
          onClick: props.onHide,
        },
        rightButton: {
          text: t('Export'),
          onClick: props.onExport,
          disabled: !anyOptionSelected,
        },
      }}
    >
      <HookFormCheckboxGroupField
        labelProps={{ text: t('ExportIssues.Label') }}
        options={exportItemOptions}
        valueField={'value'}
        labelField={'text'}
        name={'itemsToExport'}
        control={form.control}
      />
      {form.watch('itemsToExport')?.includes('issues') && (
        <HookFormRadioGroup
          labelProps={{ text: t('ExportIssues.Label') }}
          options={exportIssuesOptions}
          valueField={'value'}
          labelField={'text'}
          name={'issuesToFilter'}
          control={form.control}
        />
      )}
    </DialogModal>
  );
};
